import { useState, useEffect } from "react";
import "../assets/css/dashboard.css";
import { jwtDecode } from "jwt-decode";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Pie } from "react-chartjs-2";
import Header from "../component/header";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import configModule from '../../config.js';
import { Toaster, toast } from "react-hot-toast";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Tooltip,
  Legend
);

function Dashboard() {
  const [usersData, setUsersData] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const [dashboardCard, setDashboardCard] = useState([]);
  const [portfolioData, setPortfolioData] = useState([]);
  const config = configModule.config();
  const navigate = useNavigate();
  const paidData = new Array(12).fill(0);
  const pendingData = new Array(12).fill(0);

  const getDashboardData = async () => {

    try {
      const response = await axios.get(
        `${config.apiBaseUrl}/get-dashboarddata/${usersData?.id}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      const card = response.data?.card[0];
      const graph = response.data?.graph;
      const data = response.data?.data[0];
      setDashboardCard(card || []);
      setGraphData(graph || []);
      setPortfolioData(data || []);

    } catch (error) {
      const message =
        error.response?.data?.message ||
        error.message ||
        "Something went wrong";

      toast.error(message);
    }
  };

  useEffect(() => {
    if (usersData?.id) {
      getDashboardData();
    }
  }, [usersData]);

  Array.isArray(graphData) && graphData.forEach((item) => {
    const month = Number(item.return_month.split("-")[1]) - 1;
    const amount = Number(item["SUM(amount)"]);
  
    if (item.status === "Paid") {
      paidData[month] = amount;
    }
  
    if (item.status === "Pending") {
      pendingData[month] = amount;
    }
  });

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (token) {
      const decoded = jwtDecode(token);
      setUsersData(decoded);
    }
  }, []);

  const data = {
    labels: [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
    ],
    datasets: [
      {
        label: "Paid",
        data: paidData,
        backgroundColor: "#C7A03D",
        borderRadius: 3,
        barThickness: 18,
        categoryPercentage: 0.9,
        barPercentage: 0.9,
      },
      {
        label: "Pending",
        data: pendingData,
        backgroundColor: "#C9D5E8",
        borderRadius: 3,
        barThickness: 18,
        categoryPercentage: 0.9,
        barPercentage: 0.9,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,

    layout: {
      padding: {
        top: 30,
        right: 10,
        left: 10,
        bottom: 20,
      },
    },

    plugins: {
      legend: {
        position: "bottom",
        align: "start",

        labels: {
          usePointStyle: true,
          pointStyle: "rectRounded",
          boxWidth: 12,
          boxHeight: 12,
          padding: 25,
          color: "#51627D",
          font: {
            size: 12,
          },
        },
      },

      tooltip: {
        enabled: true,
      },
    },

    scales: {
      x: {
        grid: {
          display: false,
          drawBorder: false,
        },

        border: {
          display: false,
        },

        ticks: {
          color: "#6C7FA2",
          font: {
            size: 12,
          },
        },
      },

      y: {
        beginAtZero: true,
        max: 92,
        display: false,

        grid: {
          display: false,
        },

        border: {
          display: false,
        },
      },
    },
  };


  const phaseOneData = {
    labels: ["Vizhinjam Sea Port District", "Aero City", "Lepakshi Solar Estate"],
    datasets: [
      {
        label: "Phase 1 Interest",
        data: [75, 20, 5],
        backgroundColor: [
          "rgba(201, 168, 76, 0.7)",
          "rgba(201, 213, 232, 0.7)",
          "rgba(255, 99, 132, 0.7)",
        ],
        borderColor: [
          "rgba(201, 168, 76, 1)",
          "rgba(201, 213, 232, 1)",
          "rgba(255, 99, 132, 1)",
        ],
        borderWidth: 1
      },
    ],
  };

  const phaseOneOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        align: "start",
        labels: {
          usePointStyle: true,
          pointStyle: "circle",
          boxWidth: 10,
          boxHeight: 10,
          padding: 14,
          color: "#51627D",
          font: { size: 12 },
        },
      },
      tooltip: {
        callbacks: {
          label: (context) => `${context.label}: ${context.raw}%`,
        },
      },
    },
  };

  const formatIndianCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '₹0';

    const num = Number(value);
    if (isNaN(num)) return '₹0';

    const absNum = Math.abs(num);
    const sign = num < 0 ? '-' : '';

    let formatted;
    if (absNum >= 10000000) {
      // Crore
      formatted = (absNum / 10000000).toFixed(absNum % 10000000 === 0 ? 0 : 2) + 'Cr';
    } else if (absNum >= 100000) {
      // Lakh
      formatted = (absNum / 100000).toFixed(absNum % 100000 === 0 ? 0 : 2) + 'L';
    } else if (absNum >= 1000) {
      // Thousand
      formatted = (absNum / 1000).toFixed(absNum % 1000 === 0 ? 0 : 2) + 'K';
    } else {
      formatted = absNum.toString();
    }

    return `₹${sign}${formatted}`;
  };

  return (
    <div className="dashboardmain-st">
      <Header pageName="Dashboard" />
      <div className="body-dashboard">
        <div className="page-header">
          <div className="page-eyebrow">Overview</div>
          <div className="page-title">Good morning, {usersData?.full_name || ""}</div>
          <div className="page-subtitle">Here's your investment summary</div>
        </div>

        <div className="lxp-stats" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
          <div className="lxp-stat-card">
            <div className="lxp-stat-label">Total Invested</div>
            <div className="lxp-stat-value">{formatIndianCurrency(dashboardCard?.total_invesment) || '₹0'}</div>
          </div>
          <div className="lxp-stat-card">
            <div className="lxp-stat-label">Total Returns Received</div>
            <div className="lxp-stat-value">{formatIndianCurrency(dashboardCard?.ret_amount) || '₹0'}</div>
          </div>
          <div className="lxp-stat-card">
            <div className="lxp-stat-label">This Month's Return</div>
            <div className="lxp-stat-value">{formatIndianCurrency(dashboardCard?.cur_month_retamount) || '₹0'}</div>
          </div>
          <div className="lxp-stat-card">
            <div className="lxp-stat-label">Annual Appreciation </div>
            <div className="lxp-stat-value">-</div>
          </div>
        </div>

        <div className="lxp-dash-grid">
          <div className="lxp-card">
            <div className="lxp-card-body">
              <div className="chart-card">
                <div className="card-header-st">
                  <div>
                    <div className="card-title-st">Monthly Returns</div>
                    <div className="card-subtitle-st">Current 12 months </div>
                  </div>
                </div>

                <div className="chart-area-st">

                  <div className="chart-wrapper">
                    <Bar
                      data={data}
                      options={options}
                    />
                  </div>

                </div>

              </div>
            </div>
          </div>
          <div className="lxp-card">
            <div className="lxp-card-header">
              <div className="lxp-card-title">Diversification</div>
            </div>
            <div className="lxp-card-body d-center" style={{ height: "calc(100% - 58px)" }}>
              <div className="chart-wrapper" style={{ height: "220px" }}>
                <Pie data={phaseOneData} options={phaseOneOptions} />
              </div>
            </div>
          </div>
        </div>



        <div className="chart-card overflow-auto" style={{ background: "#fff" }}>
          <div className="card-header-st" style={{ minWidth: "732px" }}>
            <div>
              <div className="card-title-st">My Portfolio</div>
              <div className="card-subtitle-st">All active investments</div>
            </div>
            <span className="card-action-st" onClick={() => navigate("/portfolio")} >View all →</span>
          </div>

          <div className="chart-table-st" style={{ minWidth: "732px" }}>
            <div className="d-flex w-100">
              <div className="w-15 d-center comcolldbtab-st hdcoldbtab-st">Property</div>
              <div className="w-30 d-center comcolldbtab-st hdcoldbtab-st">Invested</div>
              <div className="w-25 d-center comcolldbtab-st hdcoldbtab-st">Monthly Return</div>
              <div className="w-15 d-center comcolldbtab-st hdcoldbtab-st">Since</div>
              <div className="w-15 d-center comcolldbtab-st hdcoldbtab-st">Status</div>
            </div>
            <div className="d-flex w-100">
              <div className="w-15 d-center comcolldbtab-st bdycoldbtab-st">Phase 1</div>
              <div className="w-30 d-center comcolldbtab-st bdycoldbtab-st">₹{portfolioData?.total_invesment || 0}</div>
              <div className="w-25 d-center comcolldbtab-st bdycoldbtab-st">₹{portfolioData?.mon_return} / mo</div>
              <div className="w-15 d-center comcolldbtab-st bdycoldbtab-st">{portfolioData?.created_at}</div>
              <div className="w-15 d-center comcolldbtab-st bdycoldbtab-st">{portfolioData?.status}</div>
            </div>
          </div>
        </div>

      </div>

      <Toaster
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: "#0a1628",
            color: "#fff",
            borderRadius: "10px",
            padding: "14px 18px",
          },
          success: {
            iconTheme: {
              primary: "#22c55e",
              secondary: "#fff",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "#fff",
            },
          },
        }}
      />
    </div>
  );
}

export default Dashboard;
